import { Core, easepick } from "@easepick/core";
import { RangePlugin } from "@easepick/range-plugin";
import { AmpPlugin, DateTime, LockPlugin } from "@easepick/bundle";
import { BookThatApi } from "../../BookThatApp/API";
import { IBTAProduct, IBTASearchOptions } from "../../Interfaces/BTA";
import LoaderOverlay from "../LoaderOverlay";
import { AbstractFormElement } from "../../Components/Form/Abstract";
import { Checkbox } from "../../Components/Form/Checkbox";
import { addBlackoutTooltip, holidayBlackouts, inAYear, seasonEnd, seasonStart, today } from "./Helper/DateRangePicker";
import { STORAGE_KEY_PICKED_RANGE } from "./Config";
import { BlackoutPlugin } from "../../EasePick/blackout-plugin/BlackoutPlugin";
import { RENTAL } from "./Constants";

class Rental {
    declare priceTemplate: HTMLTemplateElement;

    private page = document.querySelector(RENTAL.SELECTORS.page) as HTMLElement;
    private readonly shop: string;
    private readonly rentalCollection: string;
    private sidebar: HTMLElement;
    private productGrid: HTMLElement;
    private readonly moreFilters: HTMLElement;
    private readonly tabs: NodeListOf<HTMLElement>;
    private readonly tiles: NodeListOf<HTMLElement>;
    private productCategories: Set<string>;
    private locations: Set<string>;
    private readonly titles: [string, string][];
    private readonly powerValues: [string, number][];
    private readonly prices: [string, number][];
    private selectedCategory: string;
    private availableProducts: Set<number>;
    private tabClickHandler: () => void;
    private dateChangeHandler: () => void;
    private checkboxChangeHandler: () => void;
    private sortingHandler: () => void;
    private datePicker: HTMLElement;
    private readonly datePickerInitializer: HTMLElement;
    private easepick: Core;
    private api: BookThatApi;
    private readonly form: HTMLFormElement;
    private readonly locationInputs: NodeListOf<AbstractFormElement>;
    private startDate: string;
    private endDate: string;
    private loader: LoaderOverlay;
    private readonly submitFiltersButton: HTMLElement;
    private readonly sortingInput: AbstractFormElement;

    private readonly filterInputs: NodeListOf<AbstractFormElement>;
    private selectedFilters: { [key: string]: Set<string> };

    constructor() {
        this.shop = document.documentElement.dataset.shop;
        this.rentalCollection = this.shop === "lim-e" ? "Mietbikes" : "Mietfahrzeuge";
        this.productCategories = new Set();
        this.locations = new Set();
        this.selectedCategory = "-";
        this.availableProducts = new Set();
        this.titles = [];
        this.powerValues = [];
        this.prices = [];
        this.loader = new LoaderOverlay();
        this.submitFiltersButton = document.querySelector(RENTAL.SELECTORS.submitFilters) as HTMLElement;
        this.sortingInput = document.querySelector(RENTAL.SELECTORS.sortingInput) as AbstractFormElement;
        this.page = document.querySelector(RENTAL.SELECTORS.page) as HTMLElement;
        this.sidebar = this.page.querySelector(RENTAL.SELECTORS.sidebar) as HTMLElement;
        this.productGrid = this.page.querySelector(RENTAL.SELECTORS.productGrid) as HTMLElement;
        this.moreFilters = this.page.querySelector(RENTAL.SELECTORS.moreFilters) as HTMLElement;
        this.tabs = this.page.querySelectorAll(RENTAL.SELECTORS.tab) as NodeListOf<HTMLElement>;
        this.tiles = this.productGrid.querySelectorAll(RENTAL.SELECTORS.productTile) as NodeListOf<HTMLElement>;
        this.datePickerInitializer = this.page.querySelector(RENTAL.SELECTORS.datePickerInitializer) as HTMLElement;
        this.form = document.querySelector(RENTAL.SELECTORS.form) as HTMLFormElement;
        this.datePicker = this.form.querySelector(RENTAL.SELECTORS.datePicker) as HTMLElement;
        this.locationInputs = this.form.querySelectorAll(
            RENTAL.SELECTORS.locationInputs
        ) as NodeListOf<AbstractFormElement>;

        this.filterInputs = this.form.querySelectorAll(
            RENTAL.SELECTORS.filterInputs
        ) as NodeListOf<AbstractFormElement>;
        this.filterInputs.forEach((input) => {
            this.selectedFilters = {
                ...this.selectedFilters,
                [input.name]: new Set()
            };
        });

        this.sortingInput.value = RENTAL.DEFAULT_SORTING;

        this.initApi().then(/* suppress ignored promise warning */);

        this.addListeners()
            .collectProductDataAttributes()
            .filterEmptyCategories()
            .filterEmptyLocations()
            .createCalendar()
            .initListeners()
            .floatingButton()
            .updateProductCount()
            .disableEmptyCategories();
    }

    addListeners() {
        this.tabClickHandler = this.handleTabClick.bind(this);
        this.checkboxChangeHandler = this.handleCheckboxChange.bind(this);
        this.dateChangeHandler = this.handleDateChange.bind(this);
        this.sortingHandler = this.handleSorting.bind(this);

        return this;
    }

    async initApi() {
        this.api = new BookThatApi();
    }

    initListeners() {
        this.tabs.forEach((tab) => {
            tab.addEventListener("click", this.tabClickHandler);
        });

        this.filterInputs.forEach((input) => {
            input.addEventListener("change", this.checkboxChangeHandler);
        });

        this.sortingInput.addEventListener("change", this.sortingHandler);

        this.sidebar.querySelector(".close").addEventListener("click", (e) => this.toggleSidebar(e, false));
        this.sidebar.parentElement
            .querySelector(".product-filters-sidebar-overlay")
            .addEventListener("click", (e) => this.toggleSidebar(e, false));
        this.moreFilters.querySelector("button").addEventListener("click", (e) => this.toggleSidebar(e, true));
        this.submitFiltersButton.addEventListener("click", (e) => this.toggleSidebar(e, false));

        this.easepick.on("select", this.dateChangeHandler);
        this.easepick.on("clear", this.clearDates.bind(this));
        this.datePicker.addEventListener("change", this.dateChangeHandler);

        return this;
    }

    private handleTabClick(e: Event) {
        e.preventDefault();
        const tab = e.currentTarget as HTMLElement;
        this.selectedCategory = tab.dataset.category;
        this.tabs.forEach((tab) => tab.classList.remove("active"));
        tab.classList.add("active");

        this.filterProducts().updateProductCount();

        return this;
    }

    private async handleDateChange() {
        this.availableProducts.clear();
        /**
         * DatePicker needs YYYY-MM-DD format
         * to get this format we need to use Intl.DateTimeFormat
         * and set locale to fr-CA
         */
        const localeDateTimeInputDefault = "fr-CA";
        const startDateRaw = this.easepick.getStartDate();
        const endDateRaw = this.easepick.getEndDate();
        const defaultDateOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        } as Intl.DateTimeFormatOptions;

        const startDate =
            startDateRaw &&
            new Intl.DateTimeFormat(localeDateTimeInputDefault, defaultDateOptions).format(startDateRaw);
        const endDate = endDateRaw
            ? new Intl.DateTimeFormat(localeDateTimeInputDefault, defaultDateOptions).format(endDateRaw)
            : startDate;

        if (startDate && endDate) {
            this.startDate = startDate;
            this.endDate = endDate;
        }

        this.loader.render();
        const products = (await this.fetchProducts()) as IBTAProduct[];
        this.loader.destroy();

        products.forEach((product) => {
            this.availableProducts.add(product.external_id);
        });

        if (this.availableProducts.size === 0) {
            this.tiles.forEach((tile) => tile.classList.add(RENTAL.SELECTORS.hide));
            this.updateProductCount().disableEmptyCategories();
        } else {
            this.filterProducts().updateProductCount().disableEmptyCategories();
        }
    }

    /**
     * Handle checkboxes on the sidebar
     */
    private handleCheckboxChange(e: Event) {
        const input = e.currentTarget as Checkbox;

        this.selectedFilters[input.name][input.checked ? "add" : "delete"](input.value);

        this.filterProducts().updateProductCount().disableEmptyCategories();

        return this;
    }

    private handleSorting(e: Event) {
        const input = e.currentTarget as AbstractFormElement;
        const value = input.value;

        switch (value) {
            case "title-ascending":
                this.titles.sort((a, b) => a[1].localeCompare(b[1]));
                this.sortProducts(this.titles);
                break;
            case "title-descending":
                this.titles.sort((a, b) => b[1].localeCompare(a[1]));
                this.sortProducts(this.titles);
                break;
            case "price-ascending":
                this.prices.sort((a, b) => a[1] - b[1]);
                this.sortProducts(this.prices);
                break;
            case "price-descending":
                this.prices.sort((a, b) => b[1] - a[1]);
                this.sortProducts(this.prices);
                break;
            case "power-ascending":
                this.powerValues.sort((a, b) => a[1] - b[1]);
                this.sortProducts(this.powerValues);
                break;
            case "power-descending":
                this.powerValues.sort((a, b) => b[1] - a[1]);
                this.sortProducts(this.powerValues);
                break;
            default:
                this.tiles.forEach((tile) => (tile.style.order = null));
        }
    }

    private sortProducts(values: [string, number][] | [string, string][]) {
        let order = 0;
        values.forEach((value) => {
            const tile = [...this.tiles].find((tile) => tile.dataset.externalId === value[0]) as HTMLElement;
            if (tile) {
                tile.style.order = order.toString();
            }
            order += 10;
        });
    }

    private filterProducts() {
        this.tiles.forEach((tile) => {
            const dataset = tile.dataset;
            const externalId = +dataset.externalId;
            const category = tile.dataset.category;

            if (this.selectedCategory === category || this.selectedCategory === "-") {
                tile.classList.remove(RENTAL.SELECTORS.hide);
            } else {
                tile.classList.add(RENTAL.SELECTORS.hide);
            }

            if (
                (this.availableProducts.size !== 0 && !this.availableProducts.has(externalId)) ||
                (this.availableProducts.size === 0 && this.easepick.getEndDate() !== null)
            ) {
                tile.classList.add(RENTAL.SELECTORS.hide);
            }

            for (const key in dataset) {
                const attributeValue = dataset[key];
                if (this.selectedFilters[key] && this.selectedFilters[key].size !== 0) {
                    if (attributeValue.includes("[")) {
                        const jsonValues = JSON.parse(attributeValue);
                        if (!jsonValues.some((jsonValue: string) => this.selectedFilters[key].has(jsonValue))) {
                            tile.classList.add(RENTAL.SELECTORS.hide);
                        }
                    } else if (!this.selectedFilters[key].has(attributeValue)) {
                        tile.classList.add(RENTAL.SELECTORS.hide);
                    }
                }
            }
        });

        return this;
    }

    private disableEmptyCategories() {
        this.tabs.forEach((tab) => {
            const category = tab.dataset.category;
            const label = tab.children[0] as HTMLElement;
            const products = Array.from(this.tiles).filter(
                (tile) => tile.dataset.category === category && !tile.classList.contains(RENTAL.SELECTORS.hide)
            );
            label.dataset.disabled = (category !== "-" && products.length === 0).toString();
        });
        return this;
    }

    private updateProductCount() {
        const submitButtonText = this.submitFiltersButton.getAttribute("data-text");
        const summary = document.querySelector(`${RENTAL.SELECTORS.summary} > span`) as HTMLSpanElement;
        const i18n = JSON.parse(summary.dataset.i18n);
        const visibleProducts = Array.from(this.tiles).filter(
            (tile) => !tile.classList.contains(RENTAL.SELECTORS.hide)
        ).length;
        summary.innerText = `${visibleProducts} ${visibleProducts === 1 ? i18n.product : i18n.products}`;
        this.submitFiltersButton.querySelector("span").innerHTML = submitButtonText.replace(
            "{{ count }}",
            visibleProducts.toString()
        );
        return this;
    }

    private collectProductDataAttributes() {
        this.tiles.forEach((tile) => {
            if (!tile.classList.contains(RENTAL.SELECTORS.hide)) {
                if (tile.hasAttribute("data-category")) {
                    this.productCategories.add(tile.dataset.category);
                }
                if (tile.hasAttribute("data-location")) {
                    this.locations.add(tile.dataset.location);
                }
                if (tile.hasAttribute("data-title") && tile.hasAttribute("data-external-id")) {
                    this.titles.push([tile.dataset.externalId, tile.dataset.title]);
                }
                if (tile.hasAttribute("data-power") && tile.hasAttribute("data-external-id")) {
                    this.powerValues.push([tile.dataset.externalId, +tile.dataset.power]);
                }
                if (tile.hasAttribute("data-price") && tile.hasAttribute("data-external-id")) {
                    this.prices.push([tile.dataset.externalId, +tile.dataset.price]);
                }
            }
        });
        return this;
    }

    private filterEmptyCategories() {
        this.tabs.forEach((tab) => {
            const category = tab.dataset.category;
            if (!this.productCategories.has(category) && category !== "-") {
                tab.classList.add(RENTAL.SELECTORS.disabled);
            }
        });

        return this;
    }

    private filterEmptyLocations() {
        this.locationInputs.forEach((locationInput) => {
            if (!this.locations.has(locationInput.value)) {
                locationInput.classList.add(RENTAL.SELECTORS.hide);
            }
        });

        return this;
    }

    private async fetchProducts(): Promise<IBTAProduct[]> {
        try {
            const options = {
                start: this.startDate,
                finish: this.endDate,
                collections: [this.rentalCollection],
                per: 1000
            } as IBTASearchOptions;

            const data = await this.api.fetchSearch(options);
            return data.products ? data.products : [];
        } catch (e) {
            console.error(e);
        }
    }

    private clearDates(): void {
        this.startDate = null;
        this.endDate = null;

        this.availableProducts.clear();

        this.filterProducts().updateProductCount().disableEmptyCategories();
    }

    private toggleSidebar(e: Event, state: boolean) {
        e.preventDefault();
        document.body.classList.toggle("sidebar-open", state);
        this.sidebar.classList.toggle("open", state);
    }

    private floatingButton() {
        new IntersectionObserver(
            (entries) => {
                this.moreFilters.classList.toggle(
                    "floating",
                    !entries[0].isIntersecting && entries[0].boundingClientRect.top >= 0
                );
            },
            {
                // TODO: use precise value instead of 100px
                rootMargin: "0px 0px -100px 0px"
            }
        ).observe(this.moreFilters);

        return this;
    }

    private createCalendar() {
        // AMP module is not available in type
        // @ts-ignore
        this.easepick = new easepick.create({
            element: this.datePickerInitializer,
            css: [this.datePickerInitializer.dataset.cssPath],
            zIndex: 0,
            grid: 1,
            calendars: 1,
            inline: true,
            readonly: false,
            autoApply: false,
            scrollToDate: true,
            lang: this.datePickerInitializer.dataset.lang,
            locale: {
                cancel: this.datePickerInitializer.dataset.cancel,
                apply: this.datePickerInitializer.dataset.apply
            },
            plugins: [RangePlugin, AmpPlugin, LockPlugin, BlackoutPlugin],
            RangePlugin: {
                tooltip: false,
                repick: true,
                strict: false
            },
            LockPlugin: {
                minDate: today.isAfter(seasonStart) ? today : seasonStart,
                maxDate: seasonEnd,
                inseparable: true,
                filter: () => false
            },
            BlackoutPlugin: {
                minDate: new Date(),
                filter: (date: DateTime) =>
                    date && (date.inArray(holidayBlackouts) || date.isBefore(today) || date.isAfter(inAYear))
            },
            // @ts-ignore
            AmpPlugin: {
                resetButton: true
            },
            setup(picker) {
                picker.on("view", (e) => {
                    const { view, date, target } = e.detail;
                    if (view === "CalendarDay") {
                        addBlackoutTooltip(view, target, date);
                    }
                });
                picker.on("select", (e) => {
                    localStorage.setItem(
                        STORAGE_KEY_PICKED_RANGE,
                        JSON.stringify({
                            start: e.detail.start.valueOf(),
                            end: e.detail.end.valueOf()
                        })
                    );
                });
                picker.on("clear", () => {
                    localStorage.removeItem(STORAGE_KEY_PICKED_RANGE);
                });
            }
        });

        document.querySelector<HTMLElement>(".easepick-wrapper").dataset.sidebar = "inline";
        this.easepick.gotoDate(today.isAfter(seasonStart) ? today : seasonStart);

        return this;
    }
}

Rental.prototype.priceTemplate = document.createElement("template");
Rental.prototype.priceTemplate.innerHTML = `
    <div class="price">
        <span class="type"></span>
        <span class="value"></span>
        <span class="extra-info"></span>
    </div>
`;

const rentalPage = document.querySelector(RENTAL.SELECTORS.page) as HTMLFormElement;
if (rentalPage) {
    new Rental();
}
